import * as React from 'react';
import { Image } from '@msdyn365-commerce/core';
import { AttributeValue } from '@msdyn365-commerce/retail-proxy';
import { _getScreenSize } from '../../utilities/display-utils';

interface ICustomBadge {
    BadgeText?: string;
    BadgeBgColor?: string;
    TextColor?: string;
    StartDate?: string;
    EndDate?: string;
    BadgeImage?: string;
    __fromJson?: boolean;
}

interface IVsiCustomBadgesProps {
    context: any;
    productData: any;
    imageSettings?: any;
    isProductAvailableEcom?: boolean;
    isProductAvailableCNC?: boolean;
    isISO?: boolean;
    isComingSoon?: boolean;
    isUnbxdWidget?: boolean;
    isFetchingInventory?: boolean;
    mode?: 'text' | 'image';
    pageType?: string;
}

export const VsiCustomBadges: React.FC<IVsiCustomBadgesProps> = props => {
    const {
        context,
        productData,
        imageSettings,
        isProductAvailableEcom,
        isProductAvailableCNC,
        isISO,
        isFetchingInventory,
        isComingSoon,
        mode = 'text',
        pageType
    } = props;

    const [productAttributes, setProductAttributes] = React.useState<AttributeValue[]>([]);

    const parseDate = (dateString?: string): Date | null => {
        if (!dateString) return null;
        try {
            const cleaned = dateString.replace('GMT', '').trim();
            const [datePart, timePart] = cleaned.split(' ');
            const [day, month, year] = datePart.split('/');
            return new Date(`${year}-${month}-${day}T${timePart}`);
        } catch {
            return null;
        }
    };

    const isActive = (start?: string, end?: string): boolean => {
        const now = new Date();
        const startDate = parseDate(start);
        const endDate = parseDate(end);

        if (startDate && now < startDate) return false;
        if (endDate && now > endDate) return false;

        return true;
    };
    const screenSize = _getScreenSize(context);
    React.useEffect(() => {
        if (!productData) return;

        if (!productData?.RecordId) return;
        setProductAttributes(productData.AttributeValues || []);
    }, [productData]);

    if (!context?.app?.config?.badgeShow) return null;

    const inventoryNotReady =
        isFetchingInventory || typeof isProductAvailableEcom !== 'boolean' || typeof isProductAvailableCNC !== 'boolean';

    if (mode === 'text' && inventoryNotReady) return null;

    const allBadges: ICustomBadge[] = [];

    const jsonAttr = productAttributes.find(a => a.Name?.toLowerCase() === 'textbadgesjson');

    if (jsonAttr?.TextValue) {
        try {
            const parsed = JSON.parse(jsonAttr.TextValue);

            if (Array.isArray(parsed)) {
                parsed.forEach((item: any) => {
                    if (!item?.BadgeText?.trim()) return;

                    allBadges.push({
                        BadgeText: item.BadgeText.trim(),
                        BadgeBgColor: item.BadgeBgColor,
                        TextColor: item.TextColor,
                        StartDate: item.StartDate,
                        EndDate: item.EndDate,
                        __fromJson: true
                    });
                });
            }
        } catch {
            console.error('Invalid TextBadgesJSON');
        }
    }

    const badgeImage = productAttributes.find(a => a.Name?.toLowerCase() === 'badgeimage')?.TextValue;

    const badgeImageStart = productAttributes.find(a => a.Name?.toLowerCase() === 'badgeimagestartdate')?.TextValue;

    const badgeImageEnd = productAttributes.find(a => a.Name?.toLowerCase() === 'badgeimageenddate')?.TextValue;

    const cleanedBadgeImage = badgeImage?.trim();

    if (cleanedBadgeImage) {
        allBadges.push({
            BadgeImage: cleanedBadgeImage,
            StartDate: badgeImageStart,
            EndDate: badgeImageEnd
        });
    }

    let activeBadges: ICustomBadge[] = [];

    if (mode === 'text' && isFetchingInventory) {
        return null;
    }

    const hasInventoryData = typeof isProductAvailableEcom === 'boolean' && typeof isProductAvailableCNC === 'boolean';
    const normalizedPageType = pageType?.toLowerCase();
    const shouldHideForPLP = normalizedPageType === 'plp' && screenSize === 1;
    if (isComingSoon) {
        activeBadges.push({
            BadgeText: 'Coming soon',
            BadgeBgColor: '#F7F5EE',
            TextColor: '#C49934'
        });
    } else if (mode === 'text' && hasInventoryData && !shouldHideForPLP) {
        if (!isProductAvailableEcom && !isProductAvailableCNC) {
            activeBadges.push({
                BadgeText: 'Out of stock',
                BadgeBgColor: '#F7F5EE',
                TextColor: '#C49934'
            });
        } else if (isISO || (!isProductAvailableEcom && isProductAvailableCNC)) {
            activeBadges.push({
                BadgeText: 'Click & Collect only',
                BadgeBgColor: '#F4EA7B',
                TextColor: '#003428'
            });
        } else if (isProductAvailableEcom && !isProductAvailableCNC) {
            activeBadges.push({
                BadgeText: 'Home Delivery only',
                BadgeBgColor: '#F4EA7B',
                TextColor: '#003428'
            });
        }
    }

    allBadges.forEach(badge => {
        if (!isActive(badge.StartDate, badge.EndDate)) return;

        if (mode === 'text' && badge.BadgeText) {
            activeBadges.push(badge);
        }

        if (mode === 'image' && badge.BadgeImage && badge.BadgeImage.trim() !== '') {
            activeBadges.push(badge);
        }
    });

    let textBadges = activeBadges.filter(b => b.BadgeText);
    const systemBadgeTexts = ['Coming soon', 'Out of stock', 'Click & Collect only', 'Home Delivery only'];
    const systemBadges = textBadges.filter(b => systemBadgeTexts.includes(b.BadgeText || ''));
    const attributeBadges = textBadges.filter(b => !systemBadgeTexts.includes(b.BadgeText || ''));
    const sortByLatestStartDate = (badges: ICustomBadge[]) => {
        return badges.sort((a, b) => {
            const aDate = parseDate(a.StartDate)?.getTime() || 0;
            const bDate = parseDate(b.StartDate)?.getTime() || 0;
            return bDate - aDate;
        });
    };

    if (systemBadges.length > 0) {
        const latestAttribute = sortByLatestStartDate(attributeBadges)[0];
        textBadges = [systemBadges[0], ...(latestAttribute ? [latestAttribute] : [])].slice(0, 2);
    } else {
        textBadges = sortByLatestStartDate(attributeBadges).slice(0, 2);
    }

    const imageBadges = activeBadges.filter(b => b.BadgeImage && b.BadgeImage.trim() !== '');

    if (!textBadges.length && !imageBadges.length) return null;

    return (
        <div className='badges'>
            {mode === 'text' && textBadges.length > 0 && (
                <div className='badges__top-style'>
                    {textBadges.map((badge, i) => {
                        const style: React.CSSProperties = badge.__fromJson
                            ? {
                                  background: context.app.config.badgeBackgroundColor,
                                  color: context.app.config.badgeFontColor,
                                  fontWeight: context.app.config.badgeFontBold ? 500 : 'normal',
                                  border: context.app.config.badgeBorderShow ? '1px solid #ababac' : 'none'
                              }
                            : {
                                  background: badge.BadgeBgColor,
                                  color: badge.TextColor
                              };

                        return (
                            <div key={i} className='pdp-discount-badge badges__top' style={style}>
                                {badge.BadgeText}
                            </div>
                        );
                    })}
                </div>
            )}

            {mode === 'image' &&
                imageBadges.map((badge, i) => {
                    const imageUrl = `${context.request.apiSettings.baseImageUrl}Products/${badge.BadgeImage}`;

                    return (
                        <div key={i} className='pdp-discount-badge badges__icon'>
                            <Image
                                src={imageUrl}
                                altText=''
                                imageSettings={imageSettings}
                                gridSettings={context.request.gridSettings || {}}
                                loadFailureBehavior='hide'
                            />
                        </div>
                    );
                })}
        </div>
    );
};
